import {
  haversineMeters,
  operatorBrand,
  type SiteDetail,
  type SitePrice,
  type SiteStatus,
} from '@ev/shared'
import { useUserLocation } from '../app/useUserLocation'
import { useStrings } from '../i18n'
import {
  directionsUrl,
  formatDateTime,
  formatDistance,
  formatPrice,
  groupOpeningHours,
  isAlwaysOpen,
} from './format'
import { useLocale } from './useLocale'

/** Everything about the site that is not a station: who runs it, where it is, when it is open, what it costs. */

const Address = ({ site }: { site: SiteDetail }) => {
  const { address, admin } = site
  const houseNumber = `${address.houseNumber}${address.houseNumberAddition ?? ''}`
  // Powiat and województwo, and only the ones we have: a source that publishes
  // no administrative hierarchy would otherwise render a bare comma.
  const area = [admin.district, admin.province].filter((part) => part !== '').join(', ')

  return (
    <address className="ev-summary__address">
      <span>
        {address.street} {houseNumber}
      </span>
      <span>
        {address.postalCode} {address.city}
      </span>
      {area !== '' && <span className="ev-summary__admin">{area}</span>}
    </address>
  )
}

const OperatorLink = ({ site }: { site: SiteDetail }) => {
  const brand = operatorBrand(site.operatorSlug)

  return (
    <span className="ev-summary__operator">
      <span
        className="ev-summary__operator-dot"
        style={{ backgroundColor: brand.color }}
        aria-hidden="true"
      />
      {brand.website === undefined ? (
        brand.displayName
      ) : (
        <a href={brand.website} target="_blank" rel="noreferrer noopener">
          {brand.displayName}
        </a>
      )}
    </span>
  )
}

const OpeningHours = ({ site }: { site: SiteDetail }) => {
  const strings = useStrings()

  if (site.openingHours.length === 0)
    return <span className="ev-summary__muted">{strings.detail.openingHoursNone}</span>
  if (isAlwaysOpen(site.openingHours)) return <span>{strings.detail.alwaysOpen}</span>

  return (
    <ul className="ev-hours">
      {groupOpeningHours(site.openingHours).map((day) => (
        <li key={day.weekday} className="ev-hours__row">
          <span className="ev-hours__day">{strings.weekday[day.weekday]}</span>
          <span className="ev-hours__windows">{day.windows.join(', ')}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Distance is offered, not taken. A visitor who has already granted geolocation
 * sees the number immediately; everyone else sees a button and decides for
 * themselves whether a charging map is worth their position.
 */
const Distance = ({ site }: { site: SiteDetail }) => {
  const strings = useStrings()
  const locale = useLocale()
  const { state, position, request } = useUserLocation()

  if (position !== null)
    return (
      <span>
        {strings.detail.distanceAway(
          formatDistance(haversineMeters(position, { lat: site.lat, lon: site.lon }), locale),
        )}
      </span>
    )

  if (state === 'locating')
    return <span className="ev-summary__muted">{strings.detail.locating}</span>
  if (state === 'denied')
    return <span className="ev-summary__muted">{strings.detail.geolocationDenied}</span>
  if (state === 'failed')
    return <span className="ev-summary__muted">{strings.detail.geolocationFailed}</span>
  if (state === 'unsupported')
    return <span className="ev-summary__muted">{strings.common.none}</span>

  return (
    <button type="button" className="ev-button ev-button--quiet" onClick={request}>
      {strings.detail.showDistance}
    </button>
  )
}

const Tariff = ({ price }: { price: SitePrice | undefined }) => {
  const strings = useStrings()
  const locale = useLocale()

  const parts: string[] = []
  if (price?.pricePlnPerKwh !== undefined)
    parts.push(`${formatPrice(price.pricePlnPerKwh, locale)} ${strings.detail.pricePerKwh}`)
  if (price?.pricePlnPerMin !== undefined)
    parts.push(`${formatPrice(price.pricePlnPerMin, locale)} ${strings.detail.pricePerMinute}`)

  if (parts.length === 0 && price?.note === undefined)
    return <span className="ev-summary__muted">{strings.detail.noPrice}</span>

  return (
    <span className="ev-summary__price">
      {parts.length > 0 && <span>{parts.join(' · ')}</span>}
      {price?.note !== undefined && <span className="ev-summary__note">{price.note}</span>}
    </span>
  )
}

const StatusSummary = ({ status }: { status: SiteStatus | undefined }) => {
  const strings = useStrings()

  if (status === undefined)
    return <span className="ev-summary__muted">{strings.detail.noStatus}</span>

  const total = status.free + status.occupied + status.unavailable + status.unknown
  return <span>{strings.detail.statusCounts(status.free, total)}</span>
}

export const SiteSummary = ({
  site,
  status,
  price,
}: {
  site: SiteDetail
  status: SiteStatus | undefined
  price: SitePrice | undefined
}) => {
  const strings = useStrings()
  const locale = useLocale()
  const pointCount = site.stations.reduce((total, station) => total + station.points.length, 0)

  return (
    <div className="ev-summary">
      <p className="ev-summary__counts">
        {strings.detail.summary(site.stations.length, pointCount)}
      </p>

      <dl className="ev-summary__facts">
        <dt>{strings.detail.address}</dt>
        <dd>
          <Address site={site} />
        </dd>

        <dt>{strings.detail.operator}</dt>
        <dd>
          <OperatorLink site={site} />
        </dd>

        <dt>{strings.detail.status}</dt>
        <dd>
          <StatusSummary status={status} />
        </dd>

        <dt>{strings.detail.price}</dt>
        <dd>
          <Tariff price={price} />
        </dd>

        <dt>{strings.detail.openingHours}</dt>
        <dd>
          <OpeningHours site={site} />
        </dd>

        {site.accessibility !== '' && (
          <>
            <dt>{strings.detail.accessibility}</dt>
            <dd>{site.accessibility}</dd>
          </>
        )}

        <dt>{strings.detail.distance}</dt>
        <dd>
          <Distance site={site} />
        </dd>

        {/* Dropped whole, label included, where the source stamps nothing: a
            "last updated" row with an em dash in it reads as a value we have and
            cannot show, rather than as a date that does not exist. */}
        {site.updatedAt !== undefined && (
          <>
            <dt>{strings.detail.lastUpdated}</dt>
            <dd>{formatDateTime(site.updatedAt, locale)}</dd>
          </>
        )}
      </dl>

      <a
        className="ev-button ev-button--primary"
        href={directionsUrl(site.lat, site.lon)}
        target="_blank"
        rel="noreferrer noopener"
        title={strings.detail.directionsHint}
      >
        {strings.detail.directions}
      </a>
    </div>
  )
}
